import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { assignmentConnectionsKey } from "@/hooks/useAssignmentConnections";
import { useOrganization } from "@/hooks/useOrganization";

/**
 * Subscribes to vessel_assignment and assignment_connection realtime changes
 * for the current org. Any INSERT/UPDATE/DELETE from another user (or this user
 * on another tab) invalidates the relevant React Query caches so the planner
 * stays in sync without a manual refresh.
 *
 * Call once at the TankPlanningView root — no return value.
 */
export function usePlannerRealtime() {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  useEffect(() => {
    if (!org?.id) return;

    const channel = supabase
      .channel("planner-data-sync")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "vessel_assignment",
          filter: `org_id=eq.${org.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["vessel-assignments"] });
          queryClient.invalidateQueries({ queryKey: ["on-deck-assignments"] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "assignment_connection",
          filter: `org_id=eq.${org.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: assignmentConnectionsKey(org.id),
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [org?.id, queryClient]);
}
